import { BorderedInput, Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import React, { useEffect } from 'react';
import styles from './index.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { createEnachRequest, getEnachPresentDetails } from '../../actions/EmiScheduleActions';
import { formatAmount } from 'src/utils/commonUtils';

interface IPresentEnachDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface IPresentEnachDrawerFromValues {
  amount: string;
  comments: string;
}

const STRING_PATTERN = /^\d{2}$/;

const PresentEnachDrawer: React.FC<IPresentEnachDrawerProps> = props => {
  const { open, onClose } = props;
  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();
  const dispatch = useDispatch();
  const overDue = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.details?.data?.outstandingAmount
        .amount
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<IPresentEnachDrawerFromValues>({
    defaultValues: {
      amount: overDue?.toString() ?? '',
      comments: ''
    },
    mode: 'onChange'
  });

  const [remainingAmount, setRemainingAmount] = React.useState('--');
  const [enachAmount, setEnachAmount] = React.useState('1');
  const [creatingEnach, setCreatingEnach] = React.useState(false);

  useEffect(() => {
    if (overDue) {
      const enachAmount = overDue.toString();
      setValue('amount', enachAmount);
      setEnachAmount(enachAmount);
    }
  }, [overDue]);

  useEffect(() => {
    if (enachAmount) {
      const preDecimalValue = enachAmount.split('.')?.[0];
      const decimals = enachAmount.split('.')?.[1];

      if (decimals && decimals.length > 2) {
        const newEnachAmount = preDecimalValue + '.' + decimals.slice(0, 2);
        handleChange({ target: { value: newEnachAmount } } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  }, [enachAmount]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!overDue) return;
    const value = e.target.value;
    setEnachAmount(value);
    const remainingAmount = overDue - Number(value);
    if (Number(value) > overDue || remainingAmount <= 0 || value == '' || Number(value) < 0) {
      setRemainingAmount('--');
      return;
    }
    setRemainingAmount(remainingAmount + '');
  };
  const handleCreateEnach = (state: boolean) => {
    setCreatingEnach(state);
  };
  const successCallback = () => {
    onClose();
    dispatch(getEnachPresentDetails(customerId, loanId, caseReferenceId, caseBusinessVertical));
  };
  const submit = (data: IPresentEnachDrawerFromValues) => {
    const payload = {
      amount: Number(enachAmount),
      comments: data.comments ?? '',
      loanAccountNumber: loanId
    };
    dispatch(createEnachRequest(payload, handleCreateEnach, successCallback));
  };

  return (
    <div>
      <Drawer
        open={open}
        onClose={onClose}
        className={styles.presentEnachDrawer}
        headerText={'Present ENACH'}
        headerContainerClasses={styles.presentEnachHeader}
        footer={
          <div>
            <Button
              variant="primary"
              fullWidth
              type="submit"
              onClick={handleSubmit(submit)}
              className={styles.presentEnachButton}
              disabled={creatingEnach}
            >
              Raise Request
            </Button>
          </div>
        }
      >
        <div className={styles.amountPresentContainer}>
          <BorderedInput
            inputLabel={'Amount being paid'}
            placeholder={'Enter amount'}
            fullWidth
            value={enachAmount}
            type="number"
            error={errors.amount?.message}
            LeftInputAdornment={'₹'}
            {...register('amount', {
              valueAsNumber: true,
              required: {
                value: true,
                message: 'this field is required'
              },
              max: {
                value: overDue ?? '1',
                message: `Please enter amount less then  ${formatAmount(Number(overDue), false, 2)}`
              },
              min: {
                value: 1,
                message: 'Amount cannot be less then ₹1'
              },
              value: enachAmount?.toString() ?? '',
              pattern: {
                value: STRING_PATTERN,
                message: 'Please enter valid amount'
              },
              onChange: handleChange
            })}
          />
          <div className={styles.mt16}>
            <Typography variant="p4" className={styles.label}>
              Comments <span>(Optional)</span>
            </Typography>
            <textarea
              rows={3}
              placeholder={'Enter here'}
              {...register('comments', { required: false })}
            />
          </div>
          <div className={styles.mt16}>
            <Typography
              variant="p5"
              color="var(--navi-color-gray-c3)"
              className={styles.presentEnachText}
            >
              Remaining amount
              <strong>
                {remainingAmount == '--' ? '--' : formatAmount(+remainingAmount, false, 2)}
              </strong>
            </Typography>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default PresentEnachDrawer;
